import { useState } from 'react';
import { useUser } from '../context/UserContext';
import { useCreateMatterRequest } from './useCreateMatterRequest';
import { ICustomerType } from '../constants/customer';
import { useUpdateMatterRequest } from './useUpdateMatterRequest';
import { useManageMatterRequestContext } from '../context/ManageMatterRequest';
import { useNavigationStack } from '../context/NavigationStackContext';
import {
  buildCreateDtoForExisting,
  buildCreateDtoForNew,
  buildEditDto,
  checkHasFieldsChanged,
} from '../utils/matterRequestDtoBuilders';
import { useMatterRequestMailAsNote } from './useMatterRequestMailAsNote';
import { IMatterRequest } from '../interface/matterRequest';
import { useRestrictedUserMatterPermissions } from './useRestrictedUserMatterPermissions';
import { useVersionConflict } from './useVersionConflict';
import { fetchRecordVersion } from '../helpers/versionConflictHelper';
import { API_URLS } from '../constants/apiUrl';
import { MATTER_REQUEST_QUERY_KEYS_TO_INVALIDATE } from '../constants/matterRequest';

interface UseManageMatterRequestOptions {
  onMatterRequestCreated?: () => void;
}

export const useManageMatterRequest = (options?: UseManageMatterRequestOptions) => {
  const { onMatterRequestCreated } = options ?? {};
  const { user } = useUser();
  const {
    matterRequest,
    initialManageMatterRequest: existingMatterRequest,
    updateManageMatterRequest,
  } = useManageMatterRequestContext();
  const { goBack } = useNavigationStack();
  const { canCreateMatterRequests, canEditMatterRequests } = useRestrictedUserMatterPermissions();

  const [inactiveCustomerId, setInactiveCustomerId] = useState<string>();
  const createMatterRequestMutation = useCreateMatterRequest({
    onPrincipalCustomerInactive: ({ principalCompanyId, principalPrivatePersonId }) =>
      setInactiveCustomerId(principalCompanyId ?? principalPrivatePersonId),
  });
  const editMatterRequestMutation = useUpdateMatterRequest();
  const { latestVersion, handleVersionConflict } = useVersionConflict({
    fetchLatestVersion: async () =>
      existingMatterRequest
        ? fetchRecordVersion(`${API_URLS.CREATE_MATTER_REQUEST}/${existingMatterRequest.id}`)
        : undefined,
    queriesToInvalidate: MATTER_REQUEST_QUERY_KEYS_TO_INVALIDATE,
  });

  const {
    isMailAsNoteLoading,
    confirmationForLargeAttachmentsModal,
    showFileSizeWarning,
    handleSwitchMailAsNote,
    handleMatterRequestIsFromMailOrAppointment,
    handleConfirmLargeAttachments,
    handleCancelLargeAttachments,
    closeFileSizeWarning,
  } = useMatterRequestMailAsNote({ onMatterRequestCreated });

  const onCreateMatterRequest = () => {
    if (!user?.id || !canCreateMatterRequests) {
      return;
    }

    const createDto =
      matterRequest?.currentStepOption === 'existing'
        ? buildCreateDtoForExisting(matterRequest)
        : buildCreateDtoForNew(matterRequest, user.id);

    createMatterRequestMutation.mutate(
      { matterRequestData: createDto },
      {
        onSuccess: (createdRecord: IMatterRequest) => {
          if (matterRequest?.isFromMailOrAppointment) {
            handleMatterRequestIsFromMailOrAppointment(createdRecord);
          } else {
            updateManageMatterRequest(null, true);
            goBack();
          }
        },
      },
    );
  };
  const onEditMatterRequest = () => {
    if (!existingMatterRequest || !canEditMatterRequests) {
      return;
    }
    const editDto = {
      ...buildEditDto(matterRequest, existingMatterRequest),
      version: latestVersion ?? existingMatterRequest.version,
    };

    editMatterRequestMutation.mutate(
      { id: existingMatterRequest.id, editMatterRequestDto: editDto },
      {
        onSuccess: () => {
          updateManageMatterRequest(null, true);
          goBack();
        },
        onError: handleVersionConflict,
      },
    );
  };

  const isNewCustomerFormDisabled = !(
    matterRequest?.newCustomer?.name?.trim() &&
    (matterRequest?.newCustomer?.type === ICustomerType.COMPANY ||
      matterRequest?.newCustomer?.lastName?.trim()) &&
    matterRequest?.newCustomer?.homeCountry
  );

  const isFormDisabled =
    !matterRequest?.subject?.trim() ||
    (matterRequest?.currentStepOption === 'existing'
      ? !matterRequest?.principalCompany && !matterRequest?.principalPrivatePerson
      : isNewCustomerFormDisabled);
  const hasFieldsChanged = checkHasFieldsChanged(matterRequest);
  const selectedCustomerId =
    matterRequest?.principalCompanyId ?? matterRequest?.principalPrivatePersonId;
  const isSelectedCustomerInactive =
    matterRequest?.currentStepOption === 'existing' &&
    !!inactiveCustomerId &&
    selectedCustomerId === inactiveCustomerId;

  return {
    hasFieldsChanged,
    onCreateMatterRequest,
    onEditMatterRequest,
    isFormDisabled,
    isNewCustomerFormDisabled,
    isSelectedCustomerInactive,
    isMailAsNoteLoading,
    confirmationForLargeAttachmentsModal,
    showFileSizeWarning,
    handleSwitchMailAsNote,
    handleConfirmLargeAttachments,
    handleCancelLargeAttachments,
    closeFileSizeWarning,
  };
};
